import React, { useCallback, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import AttendanceHeader from "./AttendanceHeader";
import Text from "@components/CustomText";
import { identifyAttendanceSubject } from "@services/api";
import {
  ATTENDANCE_ROLES,
  EVENT_TYPES,
  formatAttendanceDate,
  formatShiftDuration,
} from "@utils/attendance";
import { ATTENDANCE_COLORS as C } from "./constants";
import MarkedCard from "./MarkedCard";
import RoleTile from "./RoleTile";
import { useAttendanceCapture } from "./useAttendanceCapture";
import type {
  AttendanceDetailParams,
  AttendanceEventRef,
  CaptureResponse,
  IdentifyResponse,
} from "./types";

interface Props {
  navigation: {
    goBack: () => void;
    navigate: (screen: string, params: AttendanceDetailParams) => void;
  };
  route: { name: string; params: AttendanceDetailParams };
}

const PENDING_SUBTITLE = "Pending · selfie required";

const AttendanceDetail = ({ navigation, route }: Props) => {
  const { phone } = route.params;
  const [identity, setIdentity] = useState<IdentifyResponse>(
    route.params.identity,
  );
  const [issuedAt, setIssuedAt] = useState(route.params.issuedAt);
  const [inEvent, setInEvent] = useState<AttendanceEventRef | null>(
    route.params.identity.in,
  );
  const [outEvent, setOutEvent] = useState<AttendanceEventRef | null>(
    route.params.identity.out,
  );
  const [inJustMarked, setInJustMarked] = useState(false);

  const handleSessionRefreshed = useCallback(
    (fresh: IdentifyResponse, at: number) => {
      setIdentity(fresh);
      setIssuedAt(at);
      setInEvent(fresh.in);
      setOutEvent(fresh.out);
    },
    [],
  );

  const handleUploaded = useCallback(
    async (response: CaptureResponse | null) => {
      if (response) {
        setInEvent((prev) => response.in ?? prev);
        setOutEvent((prev) => response.out ?? prev);
        if (response.in && !response.out) setInJustMarked(true);
        return;
      }
      // Body unreadable: ask the server what is on record now.
      try {
        const res = await identifyAttendanceSubject({
          role: identity.role,
          phone,
        });
        if (res?.status === 200 && res.data?.subject_token) {
          handleSessionRefreshed(res.data as IdentifyResponse, Date.now());
        }
      } catch (_e) {
        // Leave the screen as it is; the next focus/launch re-syncs.
      }
    },
    [identity.role, phone, handleSessionRefreshed],
  );

  const { mark, busy, busyEvent } = useAttendanceCapture({
    session: { identity, phone, issuedAt },
    onSessionRefreshed: handleSessionRefreshed,
    onUploaded: handleUploaded,
  });

  const isJr = identity.role === ATTENDANCE_ROLES.JR_EXECUTIVE;
  const duration =
    inEvent && outEvent
      ? formatShiftDuration(inEvent.captured_at, outEvent.captured_at)
      : null;

  return (
    <View style={styles.container}>
      <AttendanceHeader
        headertitle="Attendance"
        navigation={navigation}
        route={route}
        rightComponent={
          <Text style={styles.date}>{formatAttendanceDate(identity.date)}</Text>
        }
      />
      <View style={styles.identity} testID="attendance-identity">
        <Text style={styles.identityLabel}>
          {identity.subject.display_label ||
            (isJr ? "CP_DB ID" : "EMPLOYEE CODE")}
        </Text>
        <Text style={styles.identityValue} selectable>
          {identity.subject.display_id}
        </Text>
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.hint}>
          Mark your shift. A selfie is required for both entries.
        </Text>
        {inJustMarked && inEvent && !outEvent ? (
          <View style={styles.banner} accessibilityLiveRegion="polite">
            <Text style={styles.bannerText}>
              In attendance marked successfully
            </Text>
          </View>
        ) : null}
        {inEvent ? (
          <MarkedCard
            testID="attendance-in-marked"
            title="Mark In Attendance"
            timeLabel="In Time"
            capturedAt={inEvent.captured_at}
          />
        ) : (
          <RoleTile
            testID="attendance-in-pending"
            badge="IN"
            title="Mark In Attendance"
            subtitle={PENDING_SUBTITLE}
            marked={false}
            busy={busyEvent === EVENT_TYPES.IN}
            disabled={busy}
            onPress={() => mark(EVENT_TYPES.IN)}
          />
        )}
        {outEvent ? (
          <MarkedCard
            testID="attendance-out-marked"
            title="Mark Out Attendance"
            timeLabel="Out Time"
            capturedAt={outEvent.captured_at}
          />
        ) : (
          <RoleTile
            testID="attendance-out-pending"
            badge="OUT"
            title="Mark Out Attendance"
            subtitle={inEvent ? PENDING_SUBTITLE : "Mark In first"}
            marked={false}
            busy={busyEvent === EVENT_TYPES.OUT}
            disabled={busy || !inEvent}
            onPress={() => mark(EVENT_TYPES.OUT)}
          />
        )}
        {duration ? (
          <View style={styles.duration} testID="attendance-duration">
            <Text style={styles.durationLabel}>Shift duration</Text>
            <Text style={styles.durationValue}>{duration}</Text>
          </View>
        ) : null}
        <Pressable
          onPress={() =>
            navigation.navigate("AttendanceHistory", {
              identity,
              phone,
              issuedAt,
            })
          }
          style={styles.historyLink}
          accessibilityRole="button"
          accessibilityLabel="View history"
          testID="attendance-view-history"
        >
          <Text style={styles.historyLinkText}>View history</Text>
        </Pressable>
      </ScrollView>
    </View>
  );
};

export default AttendanceDetail;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.screenBg },
  date: { color: C.muted, fontSize: 13 },
  identity: {
    backgroundColor: C.white,
    paddingHorizontal: 18,
    paddingBottom: 14,
  },
  identityLabel: { color: C.label, fontSize: 10, letterSpacing: 1 },
  identityValue: {
    color: C.ink,
    fontSize: 16,
    fontWeight: "700",
    marginTop: 2,
  },
  content: { padding: 16, gap: 14 },
  hint: { color: C.muted, fontSize: 13 },
  banner: {
    backgroundColor: C.successBg,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  bannerText: { color: C.marked, fontSize: 14, fontWeight: "600" },
  duration: {
    backgroundColor: C.white,
    borderRadius: 14,
    paddingVertical: 16,
    paddingHorizontal: 18,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  historyLink: { alignSelf: "center", paddingVertical: 10 },
  historyLinkText: {
    color: C.ink,
    fontSize: 14,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  durationLabel: { color: C.muted, fontSize: 14 },
  durationValue: { color: C.ink, fontSize: 14, fontWeight: "700" },
});
